import { Add, HistorySharp, PersonAddAlt1, Search } from "@mui/icons-material";
import { Button, Dialog, InputAdornment, TextField } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomerCreationWizard from "../../../../components/Customer/CustomerCreationWizard";
import SimpleWidgetsHeader from "../../../../components/SimpleWidgetsHeader/SimpleWidgetsHeader";
import { PosContext } from "../../../../shared/PosContext";
import { parseCustomerAddress } from "../../../../utils/floorUtils";
import OrderDetailsDialog from "../OrderDetailsDialog";
import CustomerAddress from "./CustomerAddress";
import { useStyles } from "./styles";

const CustomerDialog = ({
    dialogOpen,
    onConfirm,
    onCancel,
    customerSelected,
    selectedCustomer,
    isTakeawayOverride,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const { customers, addresses } = useContext(PosContext);

    const [searchText, setSearchText] = useState("");
    const customerList = (customers || []).filter((customer) =>
        !searchText ||
        customer.customer_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        customer.mobile_no?.includes(searchText) ||
        customer.territory?.toLowerCase().includes(searchText.toLowerCase())
    );
    // Keep phone numbers as text so leading zeroes and formatting survive.
    const mobileSearch = /^\+?[\d\s().-]+$/.test(searchText.trim()) &&
        searchText.replace(/\D/g, "").length >= 5 ? searchText.trim() : "";
    const canAddSearchedMobile = Boolean(customers && mobileSearch && customerList.length === 0);
    const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
    const toggleOrderDetails = () => setOrderDetailsOpen(!orderDetailsOpen);
    const [orderDetailsCustomer, setOrderDetailsCustomer] = useState(null);

    const [selectedCustomerName, setSelectedCustomerName] = useState(null);
    const [customerAddressOpen, setCustomerAddressOpen] = useState(false);

    const [createCustomerDialogOpen, setCreateCustomerDialogOpen] =
        useState(false);

    useEffect(() => {
        setSelectedCustomerName(null);
        setCreateCustomerDialogOpen(false);
        setCustomerAddressOpen(false);
    }, []);

    useEffect(() => {
        if (!dialogOpen) {
            setSearchText("");
            setCreateCustomerDialogOpen(false);
        }
    }, [dialogOpen]);

    const handleOnClose = () => {
        setCustomerAddressOpen(false);
        if (onCancel) onCancel();
    };

    const onCloseCreateCustomer = () => {
        setCreateCustomerDialogOpen(false);
        setCustomerAddressOpen(false);
    };

    const showCreateCustomerDialog = () => {
        setCreateCustomerDialogOpen(true);
    };

    const onConfirmCustomerCreated = (customerName, orderData) => {
        if (customerName) {
            if (onConfirm) {
                onConfirm(customerName, orderData.address);
            }
        }

        setCreateCustomerDialogOpen(false);
    };

    // const onConfirmCustomerCreated = (customerData, addressData) => {
    //     const { name: customerName } = customerData?.data;
    //     const { name: addressName } = addressData?.data;
    //     const isTakeaway = window.location.pathname.includes("/takeaway");
    //     if (customerName) {
    //         if (onConfirm) {
    //             if (isTakeaway) {
    //                 setSelectedCustomerName(customerName);
    //                 setCustomerAddressOpen(true);
    //                 //-TODO
    //             } else {
    //             }
    //             onConfirm(customerName, addressName);
    //         }
    //     }

    //     setCreateCustomerDialogOpen(false);
    // };

    const handleCustomerSelect = (customer) => {
        const isTakeaway =
            isTakeawayOverride ??
            window.location.pathname.includes("/takeaway");

        if (isTakeaway) {
            setSelectedCustomerName(customer.name);
            // Only show address picker when multiple addresses exist.
            // If exactly one address exists, auto-select it.
            // If no addresses exist (or not loaded), continue without address.
            const customerAddresses = addresses?.[customer.name];
            if (!addresses || !customerAddresses) {
                // addresses not loaded yet -> fallback to picker
                setCustomerAddressOpen(true);
                return;
            }
            if (customerAddresses.length > 1) {
                setCustomerAddressOpen(true);
                return;
            }
            if (customerAddresses.length === 1) {
                customerSelected?.(customer.name, customerAddresses[0].name);
                return;
            }
            // No saved addresses -> open picker so user can add one.
            setCustomerAddressOpen(true);
        } else {
            setSelectedCustomerName(customer.name);
            customerSelected?.(customer.name);
        }
    };

    const handleAddressClose = () => {
        setCustomerAddressOpen(false);
    };

    const handleAddressSelect = (customerName, addressName) => {
        setCustomerAddressOpen(false);
        customerSelected?.(customerName, addressName);
    };

    let title = t("customerDialog.title");

    if (selectedCustomer) {
        title += ` [ ${selectedCustomer} ]`;
    }

    const handleViewCustomerHistory = (customer) => {
        setOrderDetailsCustomer(customer.name);
        toggleOrderDetails();
    };

    return (
        <>
            <Dialog
                fullWidth={true}
                maxWidth={"md"}
                open={dialogOpen}
                onClose={handleOnClose}
            >
                <div className={classes.DialogWrapper}>
                    <SimpleWidgetsHeader
                        Icon={PersonAddAlt1}
                        onClose={handleOnClose}
                        title={title}
                    />
                    <div className={classes.CustomerDialogActions}>
                        <div className={classes.Input_text}>
                            <div className="Input_text__Content">
                                <TextField
                                    type="text"
                                    value={searchText}
                                    autoFocus
                                    placeholder={t("customerDialog.searchPlaceholder")}
                                    onChange={(event) =>
                                        setSearchText(event.target.value)
                                    }
                                    variant="standard"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        </div>
                        <div className={classes.Input_text}>
                            <div className="Input_text__Content">
                                <Button
                                    onClick={() => showCreateCustomerDialog()}
                                >
                                    {" "}
                                    <Add /> {t("customerDialog.newCustomer")}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className={classes.List}>
                        <div className="List_Header">
                            <div className="List__Row">
                                <div>{t("customerDialog.customer")}</div>
                                <div>{t("customerDialog.phone")}</div>
                                <div>{t("customerDialog.mainAddress")}</div>
                            </div>
                        </div>

                        <div className="List_Items">
                            {canAddSearchedMobile && (
                                <Button
                                    startIcon={<PersonAddAlt1 />}
                                    onClick={showCreateCustomerDialog}
                                >
                                    {t("customerDialog.newCustomer")} — {mobileSearch}
                                </Button>
                            )}
                            {customerList.map((customer, index) => (
                                <div
                                    key={`customer-row-${index}`}
                                    style={{ display: "flex" }}
                                >
                                    <div
                                        key={"Customer_Row_" + index}
                                        className="List__Row"
                                        style={{ flex: 1 }}
                                        onClick={() =>
                                            handleCustomerSelect(customer)
                                        }
                                    >
                                        <div>{customer.customer_name}</div>
                                        <div>{customer.mobile_no}</div>
                                        <div>
                                            {parseCustomerAddress(customer)}
                                        </div>
                                    </div>
                                    <div
                                        key={"Customer_Row_Dialog_" + index}
                                        className="List__Row"
                                        style={{ marginLeft: "0.5rem" }}
                                        onClick={() =>
                                            handleViewCustomerHistory(customer)
                                        }
                                    >
                                        <HistorySharp />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Dialog>
            <Dialog
                onClose={onCloseCreateCustomer}
                open={createCustomerDialogOpen}
            >
                {createCustomerDialogOpen && <CustomerCreationWizard
                    initialMobileNo={mobileSearch}
                    onClose={onCloseCreateCustomer}
                    onConfirm={onConfirmCustomerCreated}
                />}
            </Dialog>

            <Dialog open={customerAddressOpen}>
                <CustomerAddress
                    onClose={handleAddressClose}
                    onSelect={handleAddressSelect}
                    customerName={selectedCustomerName}
                />
            </Dialog>

            <OrderDetailsDialog
                isOpen={orderDetailsOpen}
                selectedCustomer={orderDetailsCustomer}
                title={t("pos.lastOrderDetails")}
                toggle={toggleOrderDetails}
            />
        </>
    );
};

export default CustomerDialog;
